import { ReactNode, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Modal } from './Modal';
import { Form, SchemaField, FormValues } from './Form';
import { useToast } from './Toast';
import { singularize } from '../pages/detailFields';

/**
 * CreateEntityModal — generic schema-driven create/edit dialog.
 *
 * Composes Modal + Form + the /api/schema/:table endpoint to render a
 * type-aware form for any of the 60 entity tables without per-entity
 * code. Drop the component on any list page with the table name +
 * invalidate-query keys and "+ New" is wired. Pass `recordId` to
 * switch to edit mode (fetches the row + PUTs on submit; honors
 * field.immutable disable-in-edit semantics).
 *
 * Phase 11 Block 8 PR 1 (create) + PR 3 (edit) (2026-05-12).
 */

export interface CreateEntityModalProps {
  open: boolean;
  onClose: () => void;
  table: string;                                  // e.g., 'clients', 'parcels' (snake_case)
  routePath?: string;                             // e.g., 'clients' — defaults to table value with underscores → hyphens
  title?: string;                                 // override modal title (defaults: "New <table>" / "Edit <table>")
  invalidateKeys?: string[][];                    // TanStack Query keys to invalidate on success
  defaultValues?: FormValues;                     // pre-fill specific fields (e.g., property_id when adding a parcel)
  fkOptions?: Record<string, Array<{ value: string; label: string }>>;
  searchableFields?: string[];                    // FK fields rendered as a typeahead at any size
  hideFields?: string[];                          // fields hidden for this org (a runtime toggle)
  onSuccess?: (record: { id: string }) => void;   // called after successful POST/PUT
  recordId?: string;                              // when present → edit mode (fetch + PUT instead of POST)
  // Optional advisory banner rendered above the form actions, given live values
  // (e.g. a duplicate-account warning on parcel create). Advisory only.
  renderBanner?: (values: FormValues) => ReactNode;
}

interface SchemaResponse {
  table: string;
  fields: SchemaField[];
}

export function CreateEntityModal({
  open,
  onClose,
  table,
  routePath,
  title,
  invalidateKeys = [],
  defaultValues = {},
  fkOptions = {},
  searchableFields,
  hideFields,
  onSuccess,
  recordId,
  renderBanner,
}: CreateEntityModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const mode: 'create' | 'edit' = recordId ? 'edit' : 'create';

  const apiPath = (routePath ?? table).replace(/_/g, '-');

  const schemaQuery = useQuery<SchemaResponse>({
    queryKey: ['schema', table],
    queryFn: ({ signal }) => api.get<SchemaResponse>(`/api/schema/${table}`, signal),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  // Edit mode: fetch the existing record so the form pre-fills.
  const recordQuery = useQuery<Record<string, unknown>>({
    queryKey: [table, recordId],
    queryFn: ({ signal }) => api.get<Record<string, unknown>>(`/api/${apiPath}/${recordId}`, signal),
    enabled: open && mode === 'edit' && !!recordId,
  });

  const mutation = useMutation<{ id: string }, Error, FormValues>({
    mutationFn: (values: FormValues) => {
      if (mode === 'edit' && recordId) {
        return api.put<{ id: string }>(`/api/${apiPath}/${recordId}`, values);
      }
      return api.post<{ id: string }>(`/api/${apiPath}`, values);
    },
    onSuccess: (record) => {
      toast({
        variant: 'success',
        message: mode === 'edit' ? `Updated ${humanize(table)}.` : `Created ${humanize(table)}.`,
      });
      for (const key of invalidateKeys) {
        queryClient.invalidateQueries({ queryKey: key });
      }
      if (mode === 'edit' && recordId) {
        queryClient.invalidateQueries({ queryKey: [table, recordId] });
      }
      setFieldErrors({});
      onSuccess?.(record);
      onClose();
    },
    onError: (err: Error) => {
      if (err instanceof ApiClientError && err.body && typeof err.body === 'object') {
        const body = err.body as { error?: string; field?: string };
        if (body.field) {
          setFieldErrors({ [body.field]: body.error || 'Validation failed' });
        }
        toast({ variant: 'error', message: body.error || err.message });
      } else {
        toast({ variant: 'error', message: err.message });
      }
    },
  });

  const handleSubmit = (values: FormValues) => {
    const payload: FormValues = { ...values };
    for (const [k, v] of Object.entries(payload)) {
      if (v === '') payload[k] = null;
    }
    mutation.mutate(payload);
  };

  const initialValues: FormValues = mode === 'edit'
    ? { ...(recordQuery.data ?? {}), ...defaultValues }
    : defaultValues;

  const resolvedTitle = title ?? (mode === 'edit'
    ? `Edit ${humanize(table)}`
    : `New ${humanize(table)}`);

  const loading = schemaQuery.isLoading || (mode === 'edit' && recordQuery.isLoading);
  const error = schemaQuery.error || (mode === 'edit' ? recordQuery.error : null);

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!mutation.isPending) {
          setFieldErrors({});
          onClose();
        }
      }}
      title={resolvedTitle}
      size="lg"
    >
      {loading && (
        <p style={{ color: 'var(--color-ink-3)' }}>
          {schemaQuery.isLoading ? 'Loading form…' : 'Loading record…'}
        </p>
      )}
      {error && (
        <div style={{
          padding: 'var(--sp-3) var(--sp-4)',
          background: 'var(--color-status-red-bg)',
          border: '1px solid var(--color-status-red)',
          color: 'var(--color-status-red)',
          borderRadius: 'var(--r-md)',
        }}>
          Failed to load: {(error as Error).message}
        </div>
      )}
      {!loading && !error && schemaQuery.data && (
        <Form
          fields={schemaQuery.data.fields}
          initialValues={initialValues}
          mode={mode}
          fkOptions={fkOptions}
          searchableFields={searchableFields}
          hideFields={hideFields}
          errors={fieldErrors}
          submitting={mutation.isPending}
          submitLabel={mode === 'edit' ? 'Save changes' : `Create ${humanize(table)}`}
          renderBanner={mode === 'create' ? renderBanner : undefined}
          onSubmit={handleSubmit}
          onCancel={() => {
            if (!mutation.isPending) {
              setFieldErrors({});
              onClose();
            }
          }}
        />
      )}
    </Modal>
  );
}

function humanize(table: string): string {
  // Singularize the LAST word only — `invoice_term_properties` is "Invoice Term
  // Property", not "Invoice Term Propertie" and not "Invoice Term Propertys".
  const words = table.split('_');
  words[words.length - 1] = singularize(words[words.length - 1]);
  return words.map(w => w[0].toUpperCase() + w.slice(1)).join(' ');
}
